/**
 * Stages tab — multi-stage MVP orchestrator.
 *
 * Per design-system/ui_kits/mvp/admin_schedule_mvp.html: left column is
 * the stage list (stages_json metadata MERGED with the gates' actual
 * stage_index values — see stages/stageModel.buildStageViews), right
 * column is the editor for the selected stage (stages/StageEditorPane)
 * plus the revision footer (stages/RevisionBar).
 *
 * Persistence model:
 *   - Gates stay the behavioral truth. Every Save runs the same
 *     per-gate diff as the old single-stage tab — an empty gate gets
 *     POSTed, a moved gate gets PUT, a cleared gate gets DELETEd —
 *     now threaded with the selected stage_index (backend accepts
 *     0–63). Gate writes bump Event.geofence_revision and re-emit
 *     geofence_set; the phone evaluator still only arms on
 *     stage_index==0 gates (no per-stage tracking yet), which the
 *     footer copy says out loud.
 *   - Stage metadata lives in Event.stages_json, saved via PUT
 *     /events/<id>. Since M1 it is SPLIT: start_time/date/
 *     timeout_seconds are BEHAVIORAL (the backend resolves them into
 *     the payload's per-stage schedule, bumps geofence_revision and
 *     re-emits geofence_set on change — phones start on the clock and
 *     enforce per-stage timeouts); name/type/interval/laps stay
 *     cosmetic (no bump). Only written when it actually changed, so a
 *     legacy event whose operator never touches metadata keeps a NULL
 *     stages_json and behaves exactly like the old tab.
 *   - Stage 0's safety timeout maps to Event.max_stage_seconds (as
 *     before); stage >0 timeouts ride stages_json and are enforced on
 *     phones via the per-stage schedule since M1.
 *   - Circuit stages reuse <CircuitMapEditor> AS-IS (own Save button;
 *     raceline pipeline + stage-0 lap line; lap-line saves keep NOT
 *     bumping the revision).
 *
 * Single-stage compatibility: an event with no stages_json renders one
 * stage (index 0, name = event name, type = event.type) and every save
 * path matches the pre-multi-stage behavior.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Route, Save, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import {
  EventRow,
  StageMeta,
  addGate,
  deleteGate,
  updateEvent,
  updateGate,
} from '@/services/fleet';
import { useCurrentEvent } from '@/state/currentEvent';

import RevisionBar from './stages/RevisionBar';
import StageEditorPane from './stages/StageEditorPane';
import StagesListPane, { StageListItem } from './stages/StagesListPane';
import StartListCard from './stages/StartListCard';
import {
  DATE_RE,
  DEFAULT_CAR_INTERVAL_SECONDS,
  DEFAULT_LAPS,
  DEFAULT_TIMEOUT_MINUTES,
  StageFormState,
  StageView,
  TIME_RE,
  buildStageViews,
  computeStageStatus,
  findStageGate,
  findStageMeta,
  formatError,
  metaFromForm,
  nextFreeStageIndex,
  normalizeStageType,
  removeStageMeta,
  stageGates,
  stageLabel,
  stageToForm,
  upsertStageMeta,
} from './stages/stageModel';

const EMPTY_FORM: StageFormState = {
  name: '',
  type: 'Stage',
  startTime: '',
  date: '',
  carIntervalSeconds: DEFAULT_CAR_INTERVAL_SECONDS,
  timeoutMinutes: DEFAULT_TIMEOUT_MINUTES,
  laps: 10,
  start: { lat: null, lon: null, radius_m: 50 },
  finish: { lat: null, lon: null, radius_m: 50 },
};

export const StagesTab: React.FC = () => {
  // Events list + selection from the global CurrentEventProvider.
  // Navbar pill is the single source of truth; per-tab selector retired.
  const { currentEvent, refresh, loading } = useCurrentEvent();
  const [selIndex, setSelIndex] = useState(0);
  const [form, setForm] = useState<StageFormState>(EMPTY_FORM);
  // Bumped after add/delete so the form rehydrates for the SAME stage
  // index from the freshly refreshed event row.
  const [formEpoch, setFormEpoch] = useState(0);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false); // add/delete in flight
  const [error, setError] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState(false);
  const [typeSaving, setTypeSaving] = useState(false);
  const savedFlashTimer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (savedFlashTimer.current != null) window.clearTimeout(savedFlashTimer.current);
    },
    [],
  );
  const [deleteTarget, setDeleteTarget] = useState<StageView | null>(null);

  const selected = currentEvent;

  // Switching events resets the stage selection — stage indices are
  // per-event and carrying one across would select a random stage.
  useEffect(() => {
    setSelIndex(0);
    setDeleteTarget(null);
  }, [currentEvent?.id]);

  // Rehydrate the form only when the selected event id / stage index /
  // epoch changes — NOT on every 15s background poll, which produces a
  // new currentEvent reference each tick and would clobber unsaved
  // edits mid-typing (same anti-clobber semantics as the old tab).
  useEffect(() => {
    if (currentEvent) {
      setForm(stageToForm(currentEvent, selIndex));
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEvent?.id, selIndex, formEpoch]);

  const views = useMemo(
    () => (selected ? buildStageViews(selected) : []),
    [selected],
  );
  const listItems: StageListItem[] = useMemo(() => {
    if (!selected) return [];
    const now = new Date();
    return views.map((view) => ({
      view,
      status: computeStageStatus(view, selected, now),
    }));
  }, [views, selected]);
  const selectedView =
    views.find((v) => v.index === selIndex) ?? views[0] ?? null;

  // Unsaved-edits signal: the form vs a fresh canonical snapshot of the
  // selected stage. stageToForm is exactly what rehydrate loads, so a
  // plain JSON comparison is the truth (same trick as handleSave's
  // no-op diff). Drives the Save button + "Unsaved changes" hint.
  const dirty = useMemo(
    () =>
      selected != null &&
      JSON.stringify(form) !== JSON.stringify(stageToForm(selected, selIndex)),
    [form, selected, selIndex],
  );

  // ── Save (per-gate diff + metadata upsert) ──────────────────────────

  const handleSave = async () => {
    if (!selected || !selectedView) return;
    const startTime = form.startTime.trim();
    if (startTime !== '' && !TIME_RE.test(startTime)) {
      setError('Start time must be HH:MM (24h), e.g. 10:38.');
      return;
    }
    const stageDate = form.date.trim();
    if (stageDate !== '' && !DATE_RE.test(stageDate)) {
      setError('Stage date must be YYYY-MM-DD, e.g. 2026-07-01.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const original = stageToForm(selected, selIndex);

      // Per-gate diff, threaded with the selected stage_index:
      // POST new / PUT moved / DELETE cleared — identical semantics to
      // the old single-stage tab.
      for (const kind of ['start', 'finish'] as const) {
        const pos = form[kind];
        const orig = original[kind];
        const existing = findStageGate(selected.gates, kind, selIndex);
        if (pos.lat == null || pos.lon == null) {
          if (existing) {
            await deleteGate(selected.id, existing.id);
          }
        } else if (!existing) {
          await addGate(selected.id, {
            kind,
            lat: pos.lat,
            lon: pos.lon,
            radius_m: pos.radius_m,
            stage_index: selIndex,
          });
        } else if (
          pos.lat !== orig.lat ||
          pos.lon !== orig.lon ||
          pos.radius_m !== orig.radius_m
        ) {
          await updateGate(selected.id, existing.id, {
            lat: pos.lat,
            lon: pos.lon,
            radius_m: pos.radius_m,
          });
        }
      }

      // Metadata + stage-0 timeout in a single PUT, only when changed.
      // Canonicalizing both snapshots through metaFromForm makes the
      // change check a plain JSON comparison — and means an untouched
      // legacy event never grows a stages_json out of nowhere.
      const existingMeta = findStageMeta(selected, selIndex);
      const originalMeta = metaFromForm(selIndex, original, existingMeta);
      const newMeta = metaFromForm(selIndex, { ...form, startTime }, existingMeta);
      const patch: Parameters<typeof updateEvent>[1] = {};
      if (JSON.stringify(originalMeta) !== JSON.stringify(newMeta)) {
        patch.stages_json = upsertStageMeta(selected.stages_json, newMeta);
      }
      if (selIndex === 0 && form.timeoutMinutes * 60 !== selected.max_stage_seconds) {
        // Stage 0's timeout is the behavioral Event field — backend
        // treats it as a schedule change, bumps revision, re-emits.
        patch.max_stage_seconds = form.timeoutMinutes * 60;
      }
      let updated: EventRow | null = null;
      if (Object.keys(patch).length > 0) {
        updated = await updateEvent(selected.id, patch);
      }

      // Refresh the global events list (Navbar pill + other tabs see
      // the bumped geofence_revision + new gate ids). As a synchronous
      // fallback for this render, rehydrate from the PUT response.
      await refresh();
      if (updated) {
        setForm(stageToForm(updated, selIndex));
      }
      setSavedFlash(true);
      if (savedFlashTimer.current != null) window.clearTimeout(savedFlashTimer.current);
      savedFlashTimer.current = window.setTimeout(() => setSavedFlash(false), 1500);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  // ── Event type (SS1 only — own PUT, independent of the stage form's
  // Save flow so it can't get bundled/blocked by unrelated stage edits) ─

  const handleEventTypeChange = async (nextType: string) => {
    if (!selected) return;
    setError(null);
    setTypeSaving(true);
    try {
      await updateEvent(selected.id, { type: nextType });
      await refresh();
      // The stage-0 form (isCircuit, Laps vs Car interval, StartListCard
      // visibility) derives from event.type at rehydration time — bump
      // the epoch so it picks up the change immediately instead of
      // waiting for an unrelated selIndex/currentEvent.id change.
      setFormEpoch((e) => e + 1);
    } catch (err) {
      // A backend 409 (devices armed) surfaces here same as any other
      // save failure — the banner above already covers it.
      setError(formatError(err));
    } finally {
      setTypeSaving(false);
    }
  };

  // ── Add stage ───────────────────────────────────────────────────────

  const handleAddStage = async () => {
    if (!selected) return;
    const next = nextFreeStageIndex(views);
    if (next == null) {
      setError('All 64 stage slots (0–63) are in use.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // Seed the shape the event's own type implies — a circuit event's new
      // heat needs laps, not a per-car interval. `type` stays null: the event
      // governs it (buildStageView), and a stored copy could only go stale.
      const isCircuitEvent = normalizeStageType(selected.type) === 'Circuit';
      const meta: StageMeta = {
        index: next,
        name: '',
        type: null,
        start_time: null,
        date: null,
        car_interval_seconds: isCircuitEvent ? null : DEFAULT_CAR_INTERVAL_SECONDS,
        timeout_seconds: DEFAULT_TIMEOUT_MINUTES * 60,
        laps: isCircuitEvent ? DEFAULT_LAPS : null,
        reverse_of: null,
      };
      await updateEvent(selected.id, {
        stages_json: upsertStageMeta(selected.stages_json, meta),
      });
      await refresh();
      // Select the new stage so its name is immediately editable in the
      // identity card.
      setSelIndex(next);
      setFormEpoch((e) => e + 1);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  // ── Delete stage (metadata + diff-delete its gates) ─────────────────

  const handleDeleteStage = async (target: StageView) => {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      // Reuse the existing per-gate DELETE flow for every gate carrying
      // this stage_index. Only SS1 (stage 0) start/finish deletes bump
      // the revision + re-emit — stage>0 gates and lap_lines never
      // reach phones, so the backend exempts them (_phone_visible).
      // Stage 0 itself is not deletable from this UI.
      for (const g of stageGates(selected.gates, target.index)) {
        await deleteGate(selected.id, g.id);
      }
      if (findStageMeta(selected, target.index)) {
        await updateEvent(selected.id, {
          stages_json: removeStageMeta(selected.stages_json, target.index),
        });
      }
      await refresh();
      setDeleteTarget(null);
      if (selIndex === target.index && target.index !== 0) {
        setSelIndex(0);
      }
      setFormEpoch((e) => e + 1);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto mt-12 flex max-w-5xl items-center justify-center px-6">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="mx-auto mb-8 mt-4 max-w-[1280px] px-2 sm:px-6">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-center gap-3">
          <Route className="h-7 w-7 text-accent" />
          <div>
            <h1 className="text-2xl font-semibold leading-tight">Stages</h1>
            <p className="text-sm text-fg-secondary">
              Each stage = a start time + two gates. Phones enforce every stage&rsquo;s
              start time and timeout; gates arm SS1 (stage 0) only for now.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Per-tab event selector retired (Navbar pill is canonical). */}
          <Button
            onClick={handleSave}
            disabled={!selected || saving || !dirty}
            className="gap-1"
          >
            <Save className="size-4" />
            {saving ? 'Saving…' : 'Save changes'}
            {dirty && !saving && (
              <span className="ml-0.5 inline-block h-1.5 w-1.5 rounded-full bg-fg-inverse/80" />
            )}
          </Button>
          {dirty && !saving && (
            <span className="text-xs text-status-warning">Unsaved changes</span>
          )}
          {savedFlash && !dirty && <span className="text-xs text-status-online">Saved.</span>}
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded border border-status-error/40 bg-status-error/10 px-3 py-2 text-sm text-status-error">
          {error}
        </div>
      )}

      {!selected || !selectedView ? (
        <Card className="p-6">
          <CardContent className="p-0">
            <p className="text-sm text-fg-secondary">
              Create an event on the <span className="font-semibold">Event</span> tab to start
              configuring stage gates.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid items-start gap-4 lg:grid-cols-[330px_1fr]">
          <StagesListPane
            items={listItems}
            selectedIndex={selectedView.index}
            onSelect={setSelIndex}
            onAdd={handleAddStage}
            addDisabled={busy || saving}
          />
          <div className="flex flex-col gap-3.5">
            <StageEditorPane
              event={selected}
              view={selectedView}
              form={form}
              onFormChange={setForm}
              onDeleteStage={() => setDeleteTarget(selectedView)}
              onRefresh={refresh}
              onEventTypeChange={handleEventTypeChange}
              eventTypeSaving={typeSaving}
            />
            {/* M1b — per-car start list. Rally stages only: circuit
                heats start together, per-car minutes make no sense
                there. Own Save (behavioral PUT, revision bump). */}
            {form.type !== 'Circuit' && (
              <StartListCard
                event={selected}
                stageIndex={selectedView.index}
                onSaved={refresh}
              />
            )}
            <RevisionBar event={selected} />
          </div>
        </div>
      )}

      {selected && deleteTarget && (
        <Dialog open onOpenChange={(o) => !o && setDeleteTarget(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-status-error">
                <Trash2 className="size-5" />
                Delete {stageLabel(deleteTarget.index, deleteTarget.type)}
                {deleteTarget.name ? ` · ${deleteTarget.name}` : ''}?
              </DialogTitle>
            </DialogHeader>
            <DialogDescription className="text-sm text-fg-secondary">
              Removes this stage&rsquo;s schedule metadata and deletes its{' '}
              <span className="font-mono text-fg">
                {stageGates(selected.gates, deleteTarget.index).length}
              </span>{' '}
              gate(s). Only SS1 (stage 0) start/finish gates wake armed phones &mdash;
              stage&gt;0 gates never reach them, so this re-emits{' '}
              <span className="font-mono">geofence_set</span> only when SS1 gates are
              involved.
            </DialogDescription>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDeleteTarget(null)} disabled={busy}>
                Cancel
              </Button>
              <Button
                variant="outline"
                className="border-status-error text-status-error hover:bg-status-error/10"
                onClick={() => void handleDeleteStage(deleteTarget)}
                disabled={busy}
              >
                <Trash2 className="size-4" />
                {busy ? 'Deleting…' : 'Delete stage'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default StagesTab;
